import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FileText, Loader2, ShieldCheck, Upload, ExternalLink, Trash2, Undo2 } from 'lucide-react';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';
import VisualContentEditor, { type VisualContentEditorHandle } from '@/components/content-editor/VisualContentEditor';
import { isHtmlEffectivelyEmpty } from '@/utils/htmlContent';
import { courseService, type CompletionCondition, type CourseCompletionConfig } from '@/services/course.service';

interface Props {
  courseId: string;
  isPublished?: boolean;
  /** True while the Completion Config tab is showing — gates the initial fetch. */
  active?: boolean;
}

/** Imperative handle so the modal's Save Changes can flush this section. */
export interface CompletionConditionsHandle {
  /** Writes every pending change. Resolves false if validation blocked or a call failed. */
  save: () => Promise<boolean>;
}

const TERMS_TYPE = 'terms_and_conditions';
const DEFAULT_TITLE = 'Course Completion Agreement';

/**
 * Starter body for a new agreement. VisualContentEditor skips its whole init when
 * initialContent is falsy — it would then never build the document structure and
 * every edit would emit nothing — and its wipe guard reads a body with no elements
 * as a deletion. So it must never be seeded with ''.
 */
const EMPTY_TERMS_HTML = '<p></p>';

/**
 * The editor reconstructs a complete <html> document on every read. The learner
 * modal injects the stored value with dangerouslySetInnerHTML, which discards the
 * doctype/html/head/body wrapper anyway — so store just the body fragment.
 */
function toBodyFragment(html: string): string {
  const body = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return (body ? body[1] : html).trim();
}

/**
 * This HTML is rendered into the learner's page unsanitized (TermsAgreementModal
 * uses dangerouslySetInnerHTML), so scripts and event handlers are stripped here,
 * on the way in. Mirrors the allowlist the content-item preview uses.
 */
function sanitizeTermsHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'em', 'u', 's', 'b', 'i',
      'ul', 'ol', 'li', 'a', 'img', 'table', 'tr', 'td', 'th', 'thead', 'tbody',
      'tfoot', 'caption', 'colgroup', 'col',
      'pre', 'code', 'blockquote', 'br', 'hr', 'span', 'div', 'section', 'sub', 'sup',
      'small', 'mark', 'del', 'ins', 'figure', 'figcaption',
      // The editor's Font Family / Size controls run execCommand without
      // styleWithCSS, so the browser emits <font face size>. Without this the tag
      // was stripped (text kept, formatting silently dropped) and every save came
      // back unstyled — which read as "my changes didn't save".
      'font',
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'id', 'style', 'target', 'rel',
      'colspan', 'rowspan', 'width', 'height', 'loading',
      // <font> carries its formatting in attributes, not style. 'align'/'dir' and
      // the list attributes are likewise produced by the toolbar, not hand-authored.
      'face', 'size', 'color', 'align', 'dir', 'lang', 'start', 'type',
    ],
    ALLOW_DATA_ATTR: true,
  });
}

/**
 * The editor round-trips its content (normalising &nbsp;, dropping its own chrome),
 * so a freshly-loaded document rarely comes back byte-identical. Compare on a
 * whitespace-insensitive form, or merely opening the tab would look like an edit.
 */
function normalizeHtml(html: string): string {
  return html.replace(/>\s+</g, '><').replace(/\s+/g, ' ').trim();
}

/** Everything Save Changes might need to write. */
interface TermsDraft {
  custom_enabled: boolean;
  terms_enabled: boolean;
  title: string;
  source: 'pdf' | 'inline';
  html: string;
}

const EMPTY_DRAFT: TermsDraft = {
  custom_enabled: false,
  terms_enabled: false,
  title: DEFAULT_TITLE,
  source: 'inline',
  html: '',
};

function draftFromConfig(cfg: CourseCompletionConfig | null): TermsDraft {
  const condition = cfg?.conditions?.find((c) => c.type === TERMS_TYPE);
  const tc = condition?.terms_and_conditions;
  return {
    custom_enabled: cfg?.custom_enabled === true,
    terms_enabled: condition?.is_enabled === true,
    title: tc?.title || DEFAULT_TITLE,
    source: tc?.source || 'inline',
    html: tc?.html_content || '',
  };
}

/**
 * Custom completion config section on the Completion Config tab: a master switch
 * plus the Terms & Conditions condition (PDF upload or inline HTML). Nothing here
 * writes on click — every change is held locally and flushed by the modal's Save
 * Changes, exactly like the assessment ticks below it.
 */
function CompletionConditionsSection(
  { courseId, isPublished, active = true }: Props,
  ref: React.ForwardedRef<CompletionConditionsHandle>
) {
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [config, setConfig] = useState<CourseCompletionConfig | null>(null);

  // Server truth vs. what the user has staged. Save Changes writes the difference.
  const [baseline, setBaseline] = useState<TermsDraft>(EMPTY_DRAFT);
  const [draft, setDraft] = useState<TermsDraft>(EMPTY_DRAFT);
  // A PDF chosen but not uploaded yet, and an existing condition staged for removal.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [markedForRemoval, setMarkedForRemoval] = useState(false);
  // Bumped after a save so the editor remounts and re-seeds from the stored value.
  const [editorSeedKey, setEditorSeedKey] = useState(0);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const visualEditorRef = useRef<VisualContentEditorHandle>(null);

  const termsCondition: CompletionCondition | undefined =
    config?.conditions?.find((c) => c.type === TERMS_TYPE);
  const tc = termsCondition?.terms_and_conditions;

  const applyServerConfig = useCallback((cfg: CourseCompletionConfig) => {
    const next = draftFromConfig(cfg);
    setConfig(cfg);
    setBaseline(next);
    setDraft(next);
    setPendingFile(null);
    setMarkedForRemoval(false);
  }, []);

  useEffect(() => {
    if (!courseId || !active || loaded) return;
    let cancelled = false;
    setLoading(true);
    courseService
      .getCompletionConfig(courseId)
      .then((cfg) => {
        if (cancelled) return;
        applyServerConfig(cfg);
        setLoaded(true);
      })
      .catch(() => !cancelled && toast.error('Failed to load completion config'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [courseId, active, loaded, applyServerConfig]);

  /** Latest inline HTML — read from the live editor DOM when it is mounted. */
  const readInlineHtml = useCallback((): string => {
    const live = visualEditorRef.current?.getContent();
    return live ? toBodyFragment(live) : draft.html;
  }, [draft.html]);

  const save = useCallback(async (): Promise<boolean> => {
    if (isPublished || !loaded) return true;

    const inline_html = draft.source === 'inline' ? readInlineHtml() : draft.html;
    const clean_html = sanitizeTermsHtml(inline_html);
    const dirty =
      markedForRemoval ||
      !!pendingFile ||
      draft.custom_enabled !== baseline.custom_enabled ||
      draft.terms_enabled !== baseline.terms_enabled ||
      draft.title !== baseline.title ||
      // Compare the SANITIZED form: baseline.html came back from the server already
      // sanitized, so diffing raw editor output against it never matched and every
      // Save wrote a fresh version even when nothing had been touched.
      normalizeHtml(clean_html) !== normalizeHtml(baseline.html);
    if (!dirty) return true;

    // An enabled condition with nothing to show would block completion for every
    // learner on the course, with no way for them past it.
    if (draft.custom_enabled && draft.terms_enabled && !markedForRemoval) {
      const has_pdf = !!pendingFile || tc?.source === 'pdf';
      const has_inline = !isHtmlEffectivelyEmpty(clean_html);
      if (!has_pdf && !has_inline) {
        toast.error('Add the Terms & Conditions content, or turn the condition off.');
        return false;
      }
    }

    try {
      if (markedForRemoval) {
        if (termsCondition) await courseService.deleteCompletionCondition(courseId, termsCondition.id);
      } else if (draft.terms_enabled || termsCondition) {
        if (pendingFile) {
          await courseService.uploadTermsDocument(courseId, pendingFile, draft.title);
          // The upload endpoint creates the condition enabled, so an explicit
          // "off" has to be re-applied afterwards.
          if (!draft.terms_enabled) {
            const cfg = await courseService.getCompletionConfig(courseId);
            const created = cfg.conditions?.find((c) => c.type === TERMS_TYPE);
            if (created) await courseService.setConditionEnabled(courseId, created.id, false);
          }
        } else if (draft.source === 'inline' && !isHtmlEffectivelyEmpty(clean_html)) {
          await courseService.upsertTermsCondition(courseId, {
            title: draft.title,
            source: 'inline',
            html_content: clean_html,
            is_enabled: draft.terms_enabled,
          });
        } else {
          // Title / enabled only. `source` is omitted so an uploaded PDF is never
          // switched away by a save that was not about the document.
          await courseService.upsertTermsCondition(courseId, {
            title: draft.title,
            is_enabled: draft.terms_enabled,
          });
        }
      }

      if (draft.custom_enabled !== baseline.custom_enabled) {
        await courseService.setCompletionCustomEnabled(courseId, draft.custom_enabled);
      }

      applyServerConfig(await courseService.getCompletionConfig(courseId));
      setEditorSeedKey((k) => k + 1);
      return true;
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.error_description ||
          error?.message ||
          'Failed to save completion conditions'
      );
      return false;
    }
  }, [
    isPublished, loaded, draft, baseline, pendingFile, markedForRemoval,
    termsCondition, tc, courseId, readInlineHtml, applyServerConfig,
  ]);

  useImperativeHandle(ref, () => ({ save }), [save]);

  const handleFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) {
      toast.error('Only PDF files are supported');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    // 20 MB is the server limit — fail here rather than after the upload.
    if (file.size > 20 * 1024 * 1024) {
      toast.error('The document must be smaller than 20 MB');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setPendingFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading completion config...
      </div>
    );
  }

  const disabled = !!isPublished;
  // Per block, not on the section: the uploaded document row stays full strength, and
  // opacity composites the subtree so that only works if no ancestor is dimmed.
  const dim = isPublished ? 'opacity-60' : '';

  return (
    <div className="space-y-4">
      {/* Master switch */}
      <div className={`flex items-center justify-between gap-3 px-4 py-3 border border-gray-200 rounded-xl bg-white ${dim}`}>
        <div className="flex items-start gap-3 min-w-0">
          <ShieldCheck className="w-5 h-5 text-brand-indigo-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">Custom completion conditions</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Add extra gates a learner must satisfy before the course completes — beyond finishing modules.
            </p>
          </div>
        </div>
        <Toggle
          checked={draft.custom_enabled}
          disabled={disabled}
          onChange={(v) => setDraft((d) => ({ ...d, custom_enabled: v }))}
        />
      </div>

      {draft.custom_enabled && (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          {/* T&C condition header */}
          <div className={`flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200 ${dim}`}>
            <div className="flex items-center gap-2.5 min-w-0">
              <FileText className="w-4 h-4 text-gray-600 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">Terms &amp; Conditions agreement</p>
                <p className="text-xs text-gray-500">
                  After finishing all content, the learner must read and agree before completing.
                </p>
              </div>
            </div>
            <Toggle
              checked={draft.terms_enabled}
              disabled={disabled || markedForRemoval}
              onChange={(v) => setDraft((d) => ({ ...d, terms_enabled: v }))}
            />
          </div>

          {markedForRemoval ? (
            <div className="flex items-center justify-between gap-3 p-4">
              <p className="text-xs text-red-700">
                Terms &amp; Conditions will be removed when you click Save Changes.
              </p>
              <button
                type="button"
                onClick={() => setMarkedForRemoval(false)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-800"
              >
                <Undo2 className="w-3.5 h-3.5" /> Keep it
              </button>
            </div>
          ) : (
            draft.terms_enabled && (
              <div className="p-4 space-y-4">
                {/* Title */}
                <div className={dim}>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Heading shown to the learner</label>
                  <input
                    type="text"
                    value={draft.title}
                    disabled={disabled}
                    onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-indigo-500 focus:border-brand-indigo-500 disabled:bg-gray-100"
                    placeholder={DEFAULT_TITLE}
                  />
                </div>

                {/* Source selector */}
                <div className={`flex gap-2 ${dim}`}>
                  <SourceTab
                    active={draft.source === 'inline'}
                    disabled={disabled}
                    onClick={() => setDraft((d) => ({ ...d, source: 'inline' }))}
                    label="Write inline"
                  />
                  <SourceTab
                    active={draft.source === 'pdf'}
                    disabled={disabled}
                    onClick={() => setDraft((d) => ({ ...d, source: 'pdf' }))}
                    label="Upload PDF"
                  />
                </div>

                {draft.source === 'inline' ? (
                  <div className={`space-y-2 ${dim}`}>
                    {/* Same authoring surface as lesson content, cut down to the Home
                        ribbon: an agreement is written and formatted, not composed from
                        media, tables or learning components. Format is dropped without
                        loss — its four controls all also live in Home. */}
                    <VisualContentEditor
                      key={editorSeedKey}
                      ref={visualEditorRef}
                      // Seeded from the DRAFT, not the baseline. Switching to the
                      // Upload PDF tab unmounts this whole branch, so coming back
                      // re-seeds from scratch — off the baseline that meant every
                      // unsaved edit was silently rolled back to server truth.
                      // initialContent is only read on mount / key change, so this
                      // does not fight the editor on each keystroke.
                      initialContent={draft.html || EMPTY_TERMS_HTML}
                      onChange={(html) => setDraft((d) => ({ ...d, html: toBodyFragment(html) }))}
                      readOnly={disabled}
                      hideInsertTab
                      hideFormatTab
                      hideVideoInsert
                      hideAudioInsert
                      hideKnowledgeCheck
                      className="!max-h-none [&_.editor-content-preview]:!min-h-[240px] [&_.editor-content-preview]:!max-h-[380px]"
                    />
                    {/* <p className="text-[11px] text-gray-400">
                      Formatting is preserved and rendered to the learner before they can complete the course.
                      {tc?.source === 'inline' && ` Saved version ${tc.version}.`}
                    </p> */}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingFile ? (
                      <div className="flex items-center justify-between gap-3 px-3 py-2.5 border border-amber-200 rounded-lg bg-amber-50">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-4 h-4 text-amber-600 flex-shrink-0" />
                          <span className="text-sm text-gray-800 truncate">{pendingFile.name}</span>
                          <span className="text-[11px] text-amber-700 flex-shrink-0">uploads on Save Changes</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setPendingFile(null)}
                          className="text-xs font-medium text-gray-600 hover:text-gray-800 flex-shrink-0"
                        >
                          Clear
                        </button>
                      </div>
                    ) : tc?.source === 'pdf' && tc.file_name ? (
                      <div className="flex items-center justify-between gap-3 px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                          <span className="text-sm text-gray-800 truncate">{tc.file_name}</span>
                          {/* <span className="text-[11px] text-gray-400 flex-shrink-0">v{tc.version}</span> */}
                        </div>
                        {/* Always live, published or not — viewing the agreement is
                            not an edit, and the fieldset that freezes this tab only
                            disables form controls anyway. */}
                        {tc.document_url && (
                          <a
                            href={tc.document_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-medium text-brand-indigo-600 hover:text-brand-indigo-700 hover:underline !cursor-pointer flex-shrink-0"
                          >
                            View <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    ) : (
                      <p className={`text-xs text-gray-500 ${dim}`}>No document uploaded yet.</p>
                    )}

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="application/pdf"
                      onChange={handleFilePicked}
                      disabled={disabled}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={disabled}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 ${dim}`}
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {tc?.source === 'pdf' && tc.file_name ? 'Replace PDF' : 'Choose PDF'}
                    </button>
                  </div>
                )}

                {/* Remove */}
                {termsCondition && !isPublished && (
                  <button
                    type="button"
                    onClick={() => setMarkedForRemoval(true)}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove Terms &amp; Conditions
                  </button>
                )}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

export default forwardRef<CompletionConditionsHandle, Props>(CompletionConditionsSection);

function Toggle({ checked, disabled, onChange }: { checked: boolean; disabled?: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        checked ? 'bg-brand-indigo-600' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

function SourceTab({ active, disabled, onClick, label }: { active: boolean; disabled?: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50 ${
        active
          ? 'bg-brand-indigo-50 border-brand-indigo-300 text-brand-indigo-700'
          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
      }`}
    >
      {label}
    </button>
  );
}
